import React, { FC, useCallback, useRef } from 'react';
import { ErrorMessage } from 'shared/components/Input/ErrorMessage';
import { mergeClasses } from 'shared/utils/mergeClasses';
import { InputErrorType } from 'types/InputErrorType';

interface OTPInputProps {
  length: number;
  value: string;
  onChange: (otp: string) => void;
  error?: InputErrorType;
  disabled?: boolean;
  isTranslucent?: boolean;
  className?: string;
}

export const OTPInput: FC<OTPInputProps> = ({
  length,
  value,
  onChange,
  error,
  disabled,
  isTranslucent = false,
  className = '',
}) => {
  const inputsRef = useRef<(HTMLInputElement | null)[]>([]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
      const char = e.target.value
        .slice(-1)
        .replace(/[^A-Za-z0-9]/g, '')
        .toUpperCase();

      if (!char) {
        return;
      }

      const arr = value.split('');
      arr[index] = char;
      onChange(arr.join(''));

      // if there's space to move to and no empty space behind
      if (index < length - 1 && (index === 0 || arr[index - 1] !== undefined)) {
        inputsRef.current[index + 1]?.focus();
      }
    },
    [length, onChange, value],
  );

  // backspace and arrows
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
      if (e.key === 'Backspace') {
        e.preventDefault();
        const arr = value.split('');
        if (arr[index]) {
          // clear this cell
          arr[index] = '';
          onChange(arr.join(''));
        } else {
          // clear last cell if present
          if (arr[index - 1]) {
            arr[index - 1] = '';
          }
          inputsRef.current[index - 1]?.focus();
          onChange(arr.join(''));
        }
      } else if (e.key === 'ArrowLeft' && index > 0) {
        e.preventDefault();
        inputsRef.current[index - 1]?.focus();
      } else if (e.key === 'ArrowRight' && index < length - 1) {
        e.preventDefault();
        inputsRef.current[index + 1]?.focus();
      }
    },
    [length, onChange, value],
  );

  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLInputElement>) => {
      e.preventDefault();
      const pastedData = e.clipboardData.getData('Text').replace(/[^a-zA-Z0-9]/g, '');
      if (pastedData.length !== length) {
        return;
      }

      onChange(pastedData.toUpperCase());

      inputsRef.current[length - 1]?.focus();
    },
    [length, onChange],
  );

  return (
    <div>
      <div className={mergeClasses('flex h-12 w-full justify-between gap-1 lg:gap-2', className)}>
        {Array.from({ length }).map((_, index) => (
          <input
            key={index}
            ref={(el) => {
              inputsRef.current[index] = el;
            }}
            type="text"
            disabled={disabled}
            value={value[index] || ''}
            onChange={(e) => handleChange(e, index)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            onPaste={handlePaste}
            className={mergeClasses(
              'h-12 w-[2.875rem] appearance-none rounded-lg border text-center text-lg caret-gray outline-none focus-visible:border-gray disabled:cursor-not-allowed lg:w-[3.25rem]',
              isTranslucent ? 'border-opacity-50 bg-white bg-opacity-[0.08]' : '',
              error ? 'border-red' : 'border-gray-300',
            )}
          />
        ))}
      </div>
      {error && <ErrorMessage>{error.message}</ErrorMessage>}
    </div>
  );
};
